import { h, defineComponent } from 'vue';
import { t, du, n } from '@/ui/i18n/i18n';
import { readOnlyField } from '@/ui/components/form/ReadOnlyField';
import { SensorMetric, SensorMetricSymbol } from '@/entities/sensor/constants';
import { NumberFormatter } from '@/ui/i18n/numberFormats';

export interface IssueDetailsSensorContentProps {
  name: string;
  metric: SensorMetric;
  value: number | null;
  timestamp: number | null;
}

export const IssueDetailsSensorContent = defineComponent<IssueDetailsSensorContentProps>({
  name: 'IssueDetailsSensorContent',
  props: ['name', 'metric', 'value', 'timestamp'],
  setup(props) {
    const getNumberMetric = (): NumberFormatter => {
      return props.metric === SensorMetric.Temperature ? 'metricTemperature' : 'metricHumidity';
    };

    const getMetricSymbol = (): string => {
      return props.metric === SensorMetric.Temperature ? SensorMetricSymbol.Temperature : '';
    };

    // Why do we need to divide humidity metric value by 100?
    // Humidity is given in percentage. The BE sends it as 70.99, for example. But the number formatting function
    // expects something between 0 and 1 for percentage numbers or 00.7799 for our example. So, in order to use the
    // formatting number here, we should divide it by 100.
    const getMetricValue = (value: number): number => {
      return props.metric === SensorMetric.Temperature
        ? value
        : value / 100;
    };

    return function render() {
      return h('div', [
        readOnlyField({
          name: 'sensorName',
          label: t('common.sensor'),
          value: props.name,
        }),
        readOnlyField({
          name: 'sensorValue',
          label: t(`sensors.${props.metric}`),
          value: props.value ? `${n(getMetricValue(props.value), getNumberMetric())} ${getMetricSymbol()}` : '',
        }),
        readOnlyField({
          name: 'measurementTime',
          label: t('sensors.measurement-time'),
          value: props.timestamp ? du(props.timestamp, 'shortDateTime') : '',
        }),
      ]);
    };
  },
});
